import React from "react";
import { useNavigate } from "react-router-dom";
import { Users as UsersIcon, Clock, Layers, BookOpen, ChevronRight, ChevronDown, ChevronLeft, FileText, Play, Pause, Check, AlertTriangle } from "lucide-react";
import { DashAntd } from "../../../components/DashAntd";
import { fmtMins } from "../../../components/classic/micro";
import { Prose, LiveStretchFacts } from "../../../components/drilldown/WorkLog";
import { GPanel, PanelHead } from "../parts";
import { RaceRows, inks, paper, prettyDay, toolColors } from "../viz";
import { ToolIcon } from "../../../components/classic/panels";
import { Avatar } from "../../../components/primitives";
import { InfoDot } from "../../../components/classic/InfoDot";
import { DrillFrame } from "./DrillFrame";
import { useSurface } from "../../growthSurface";
import { WorkWriteup, WorkAppAnalysis } from "./WorkWriteup";
import { KpiStrip, SkeletonRows } from "./StoryChrome";
import { TimeRail, railFromStretches } from "./TimelineKit";
import { DASHBOARD_BASE } from "../../../../../config/featureFlags";

/* ── zoom-instrument plumbing ─────────────────────────────────────────────
   Segments are REAL time blocks only: every s/e below comes from a fetched
   start time plus a measured duration. Nothing is invented — views without
   per-block times pass no segments and the instrument hides itself. */

const SEG_INKS = ["blue", "violet", "gold", "pink"];

/** "HH:MM(:SS)" (24h, or with am/pm) → minutes since midnight, or null. */
function parseClockMin(v) {
  const m = /^\s*(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)?/i.exec(String(v || ""));
  if (!m) return null;
  let h = Number(m[1]);
  const mm = Number(m[2]);
  const ap = (m[3] || "").toLowerCase();
  if (ap === "pm" && h < 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  if (h > 23 || mm > 59) return null;
  return h * 60 + mm;
}

/** Minutes since midnight → "H:MM". */
function fmtClockMin(mins) {
  let h = Math.floor(mins / 60);
  let m = Math.round(mins % 60);
  if (m === 60) { h += 1; m = 0; }
  h = ((h % 24) + 24) % 24;
  return `${h}:${String(m).padStart(2, "0")}`;
}

/**
 * Fetched blocks ({ t0, minutes, title, … }) → instrument segments.
 * Ink is assigned per distinct title so one purpose keeps one colour across
 * the track; ids are deterministic so drilling deeper keeps the same segment
 * identities and the viewport change animates as a zoom.
 */
function segmentsFromBlocks(blocks, fallbackTitle) {
  const inkByTitle = new Map();
  const out = [];
  (blocks || []).forEach((b, i) => {
    const s = parseClockMin(b.t0);
    const mins = Math.round(Number(b.minutes) || 0);
    if (s == null || mins <= 0) return;
    const e = Math.min(s + mins, 24 * 60);
    if (e <= s) return;
    const title = b.title || fallbackTitle || "Work";
    if (!inkByTitle.has(title)) inkByTitle.set(title, SEG_INKS[inkByTitle.size % SEG_INKS.length]);
    out.push({
      id: `${b.userId || "u"}|${b.dateLocal || b.day || ""}|${s}|${i}`,
      s,
      e,
      ink: inkByTitle.get(title),
      label: `${title}${b.who ? ` — ${b.who}` : ""}, ${fmtClockMin(s)} to ${fmtClockMin(e)}`,
      title,
      minutes: mins,
    });
  });
  return out;
}

function SectionBar({ t, tone, icon, title, bits, info }) {
  const ink = inks(t)[tone] || inks(t).blue;
  const ppr = paper(t);
  const band = `color-mix(in srgb, ${ink} ${t.isDark ? "20%" : "11%"}, ${ppr.card})`;
  return (
    <div style={{
      margin: "-17px -18px 14px",
      padding: "12px 16px",
      background: band,
      borderBottom: `1px solid ${t.c.border}`,
      borderRadius: `${t.r.card} ${t.r.card} 0 0`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span aria-hidden="true" style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          display: "grid", placeItems: "center", background: ink, color: "#fff",
        }}>{icon}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 650, letterSpacing: "-0.2px", color: t.c.text }}>{title}</h3>
          {bits?.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 3 }}>
              {bits.map((b) => (
                <span key={b.label} style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  fontSize: 12, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums",
                }}>
                  <span style={{ display: "grid" }}>{b.icon}</span>
                  {b.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        {info ? <InfoDot metric={info} tone="graphical" /> : null}
      </div>
    </div>
  );
}

function firstName(name) {
  return String(name || "").trim().split(/\s+/)[0] || "They";
}

function cleanLine(s) {
  return String(s || "")
    .replace(/\s+and\s+\d+\s+more\s+pages?$/i, "")
    .replace(/\s*[-–—|]\s*(Google Chrome|Microsoft Edge|Mozilla Firefox|Safari|Brave|Chrome|Edge)\s*$/i, "")
    .replace(/\s*\(\d{1,5}\)\s*$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function clockHM(t0) {
  const m = parseClockMin(t0);
  if (m == null) return "—";
  const h = Math.floor(m / 60) % 12 || 12;
  return `${h}:${String(m % 60).padStart(2, "0")}`;
}

function clockShort(v) {
  const n = parseClockMin(v);
  if (n == null) return v || "—";
  return `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
}

function startMsOf(day, t0) {
  const m = parseClockMin(t0);
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ""));
  if (m == null || !d) return null;
  return new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Math.floor(m / 60), m % 60).getTime();
}

function clockRange(t0, t1, minutes) {
  const a = parseClockMin(t0);
  const b = parseClockMin(t1);
  const fmt = (n) => `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
  const mins = Number(minutes) > 0 ? Number(minutes) : (a != null && b != null && b > a ? b - a : 0);
  if (a == null) return mins ? fmtMins(mins) : "";
  if (b == null || b <= a) return mins ? `${fmt(a)} · ${fmtMins(mins)}` : fmt(a);
  return `${fmt(a)} → ${fmt(b)}`;
}

function isBrowserTool(name) {
  return /chrome|edge|firefox|safari|brave|arc|opera/i.test(String(name || ""));
}

function sittingWhen(t0) {
  const m = parseClockMin(t0);
  if (m == null) return "";
  if (m < 12 * 60) return "Morning";
  if (m < 17 * 60) return "Afternoon";
  return "Evening";
}

function sittingTitle(row, toolName) {
  return sittingOverview(row, toolName);
}

function firstSentence(text) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (!s) return "";
  const cut = s.split(/(?<=[.!?])\s+/)[0] || s;
  return cut.length > 140 ? `${cut.slice(0, 132).trim()}…` : cut;
}

function isGenericTitle(s, toolName) {
  const low = cleanLine(s).toLowerCase();
  const tool = String(toolName || "").toLowerCase();
  if (!low) return true;
  if (tool && (low === tool || low === `time in ${tool}` || low === `one sitting in ${tool}`)) return true;
  if (NOISE_TAB.test(low) || /^time in /i.test(low)) return true;
  return false;
}

/** What actually happened — shown on the card face, never “Time in Teams”. */
function sittingOverview(st, toolName) {
  const raw = st?.raw || st?.hops?.[0]?.raw || {};
  const summary = firstSentence(raw.summary || st?.summary);
  if (summary && !isGenericTitle(summary, toolName)) return summary;
  const acts = raw.activities || st?.activities || [];
  const det = acts
    .map((a) => cleanLine(a?.detail || a?.label))
    .find((d) => d && !isGenericTitle(d, toolName));
  if (det) return firstSentence(det);
  const hopLine = (st?.hops || [])
    .map((h) => cleanLine(h.title))
    .find((line) => line && !isGenericTitle(line, toolName));
  if (hopLine) return hopLine;
  const title = cleanLine(raw.title || st?.title);
  if (title && !isGenericTitle(title, toolName)) return title;
  const mins = sittingMins(st);
  if (mins) return `${fmtMins(mins)} in ${toolName || "this tool"} — nothing named yet`;
  const when = sittingWhen(st?.t0);
  return when ? `${when} sitting` : "Time tracked, nothing named yet";
}

function sittingMins(st) {
  const n = Number(st?.minutes) || hopMins(st) || 0;
  if (n > 0) return n;
  if (st?.hops?.length) {
    return mergedMins(st.hops) || st.hops.reduce((a, h) => a + (hopMins(h) || Number(h.minutes) || 0), 0);
  }
  return 0;
}

function sitTile(st) {
  const mins = sittingMins(st);
  const clock = clockHM(st?.t0);
  if (clock !== "—") return { top: clock, bot: mins ? fmtMins(mins) : "" };
  return { top: mins ? fmtMins(mins) : "—", bot: sittingWhen(st?.t0) };
}

function sitWhenLine(st) {
  if (st?.t0) return clockRange(st.t0, st.t1, sittingMins(st));
  if (st?.dateLocal) return prettyDay(st.dateLocal);
  return sittingMins(st) ? fmtMins(sittingMins(st)) : "";
}

function siteFromTitle(title, toolName) {
  const raw = cleanLine(title);
  if (!raw) return null;
  const url = raw.match(/https?:\/\/([^/\s]+)/i) || raw.match(/\b([a-z0-9-]+\.[a-z]{2,}(?:\.[a-z]{2,})?)\b/i);
  if (url) return String(url[1] || url[0]).replace(/^www\./, "");
  const cut = raw.split(/\s+[–—|-]\s+/)[0].trim();
  const low = cut.toLowerCase();
  if (!cut || low === String(toolName || "").toLowerCase() || low === "google chrome" || low === "chrome") return null;
  return cut.slice(0, 42);
}

function sittingsForPerson(uid, stretches, activity, timeline, name) {
  const mine = (stretches || []).map((st, i) => ({ st, i }))
    .filter(({ st }) => String(st.userId) === String(uid));
  const named = mine.filter(({ st }) => st.mentions !== false);
  const fromSt = (named.length ? named : mine).map(({ st, i }) => ({
    key: `st-${i}`,
    title: cleanLine(st.title) || firstSentence(st.summary) || "",
    summary: st.summary,
    t0: st.t0,
    t1: st.t1,
    minutes: st.minutes,
    durationLabel: st.durationLabel,
    dateLocal: st.dateLocal,
    kind: "stretch",
    index: i,
    raw: st,
    site: siteFromTitle(st.title, name),
  }));

  const fromWin = [];
  for (const a of (activity || []).filter((x) => String(x.userId) === String(uid))) {
    const wins = a.windows?.length ? a.windows : [{ title: a.title, minutes: a.minutes, t0: a.t0, t1: a.t1 }];
    wins.forEach((w, wi) => {
      fromWin.push({
        key: `w-${a.dateLocal}-${wi}`,
        title: cleanLine(w.title) || `Time in ${name}`,
        t0: w.t0,
        t1: w.t1,
        minutes: w.minutes,
        dateLocal: a.dateLocal,
        kind: "usage",
        raw: a,
        url: w.url,
        site: siteFromTitle(w.title, name),
      });
    });
  }
  if (fromSt.length && fromWin.length) {
    const covered = fromSt.map((r) => {
      const s = parseClockMin(r.t0);
      const e = parseClockMin(r.t1) ?? (s != null ? s + (Number(r.minutes) || hopMins(r) || 1) : null);
      return s != null && e != null ? [s, e, r.dateLocal] : null;
    }).filter(Boolean);
    const extra = fromWin.filter((w) => {
      const s = parseClockMin(w.t0);
      const e = parseClockMin(w.t1) ?? (s != null ? s + (Number(w.minutes) || 1) : null);
      if (s == null || e == null) return false;
      return !covered.some(([a, b, day]) => day === w.dateLocal && b > s && a < e);
    });
    return extra.length ? [...fromSt, ...extra] : fromSt;
  }
  if (fromSt.length) return fromSt;
  if (fromWin.length) return fromWin;

  return (timeline || [])
    .filter((e) => String(e.userId) === String(uid))
    .map((e, i) => ({
      key: `t-${i}`,
      title: cleanLine(e.title) || `Time in ${name}`,
      t0: e.t0,
      minutes: e.minutes,
      dateLocal: e.dateLocal,
      kind: "usage",
      raw: e,
      url: e.url,
      site: siteFromTitle(e.title, name),
    }));
}

const SITE_FAMILIES = [
  [/gmail|^inbox\b|mail\.google/i, "gmail", "Gmail"],
  [/meet\.google|^meet\b|google meet/i, "meet", "Google Meet"],
  [/teamgrid|localhost:3000/i, "teamgrid", "TeamGrid"],
  [/docs\.google|google docs/i, "docs", "Google Docs"],
  [/sheets\.google|google sheets/i, "sheets", "Google Sheets"],
  [/calendar\.google|google calendar/i, "calendar", "Google Calendar"],
  [/chatgpt|openai\.com/i, "chatgpt", "ChatGPT"],
  [/github/i, "github", "GitHub"],
  [/slack/i, "slack", "Slack"],
  [/notion/i, "notion", "Notion"],
];
const NOISE_TAB = /^(new tab|untitled|about:blank|chrome:\/\/.*)$/i;

function foldKey(title, toolName) {
  const s = cleanLine(title);
  const low = s.toLowerCase();
  if (!s || NOISE_TAB.test(low) || low === String(toolName || "").toLowerCase()) return "other";
  const hit = SITE_FAMILIES.find(([rx]) => rx.test(s));
  if (hit) return hit[1];
  return (siteFromTitle(s, toolName) || s).toLowerCase().slice(0, 48);
}

function foldLabel(key, sample, toolName) {
  const hit = SITE_FAMILIES.find(([, id]) => id === key);
  if (hit) return hit[2];
  if (key === "other") return isBrowserTool(toolName) ? "Other tabs" : sittingTitle({ title: sample }, toolName);
  return cleanLine(sample) || key;
}

function mergedMins(hops) {
  const ranges = hops.map((h) => {
    const a = parseClockMin(h.t0);
    const b = parseClockMin(h.t1) ?? (a != null ? a + (Number(h.minutes) || 0) : null);
    return a != null && b != null && b > a ? [a, b] : null;
  }).filter(Boolean).sort((x, y) => x[0] - y[0]);
  if (!ranges.length) return hops.reduce((n, h) => n + (Number(h.minutes) || 0), 0);
  let total = 0;
  let [c0, c1] = ranges[0];
  for (let i = 1; i < ranges.length; i += 1) {
    const [a, b] = ranges[i];
    if (a <= c1) c1 = Math.max(c1, b);
    else { total += c1 - c0; c0 = a; c1 = b; }
  }
  return total + (c1 - c0);
}

function hopMins(hop) {
  const a = parseClockMin(hop?.t0);
  const b = parseClockMin(hop?.t1);
  if (a != null && b != null && b > a) return b - a;
  return Number(hop?.minutes) || 0;
}

const PATH_TOPICS = [
  [/graphical\/tool/, "Tool page"],
  [/graphical\/person/, "Person page"],
  [/graphical\/project/, "Project page"],
  [/work-summary/, "Work summary"],
  [/app-usage/, "App usage"],
  [/org-intelligence/, "Org intelligence"],
  [/\/team\b/, "Team"],
  [/\/tasks\b/, "Tasks"],
  [/mail\.google|\/mail\b/, "Inbox"],
  [/calendar\.google/, "Calendar"],
  [/docs\.google/, "Doc"],
  [/meet\.google/, "Call"],
];

function pathOf(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  try {
    const href = /^https?:/i.test(s) ? s : s.includes("/") ? `https://${s.replace(/^\/+/, "")}` : "";
    if (href) return decodeURIComponent(new URL(href).pathname || "");
  } catch (_e) { /* title, not a url */ }
  const m = s.match(/\/[a-z0-9/_-]+/i);
  return m ? m[0] : "";
}

function hopTopic(hop, parentTitle, index, total) {
  const path = pathOf(hop?.url || hop?.title);
  const fromPath = PATH_TOPICS.find(([rx]) => rx.test(path));
  if (fromPath) return fromPath[1];
  if (path && path !== "/" && path.split("/").filter(Boolean).length) {
    const last = path.split("/").filter(Boolean).pop();
    if (last && last.length > 2 && last.length < 28) {
      return last.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    }
  }
  let line = cleanLine(hop?.title);
  const parent = String(parentTitle || "").trim();
  if (line && parent) {
    const cut = new RegExp(`^${parent.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*[–—|:·-]\\s*`, "i");
    line = line.replace(cut, "").trim();
  }
  const low = line.toLowerCase();
  const plow = parent.toLowerCase();
  if (line && low !== plow && !(low.startsWith(plow) && line.length <= parent.length + 3)) return line.slice(0, 48);
  const m = parseClockMin(hop?.t0);
  if (total <= 1) return parent || "Visit";
  if (m == null) return `Visit ${index + 1}`;
  if (m < 12 * 60) return index === 0 ? "Morning" : "Late morning";
  if (m < 17 * 60) return "Afternoon";
  return "Evening";
}

function groupWhen(hops) {
  const sorted = [...hops].sort((a, b) => (parseClockMin(a.t0) || 0) - (parseClockMin(b.t0) || 0));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (sorted.length === 1) return clockRange(first.t0, first.t1, hopMins(first) || first.minutes);
  let gap = 0;
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = parseClockMin(sorted[i - 1].t1) ?? parseClockMin(sorted[i - 1].t0);
    const next = parseClockMin(sorted[i].t0);
    if (prev != null && next != null) gap = Math.max(gap, next - prev);
  }
  if (gap > 40) return clockHM(first.t0);
  return clockRange(first.t0, last.t1, mergedMins(sorted));
}

/** One card per site per day — Inbox (484) + Inbox + New Tab do not each get a row. */
function foldSittings(rows, toolName) {
  if (!isBrowserTool(toolName)) {
    return (rows || []).map((r, i) => {
      const mins = sittingMins(r);
      return {
        key: r.key || `sit-${i}`,
        fam: "sit",
        hops: [r],
        dateLocal: r.dateLocal,
        kind: r.kind,
        index: r.index,
        raw: r.raw,
        title: sittingOverview(r, toolName),
        overview: sittingOverview(r, toolName),
        t0: r.t0,
        t1: r.t1,
        minutes: mins,
        durationLabel: r.durationLabel || (mins ? fmtMins(mins) : null),
      };
    }).sort((a, b) => (parseClockMin(a.t0) || 0) - (parseClockMin(b.t0) || 0));
  }
  const map = new Map();
  const order = [];
  for (const r of rows || []) {
    const fam = foldKey(r.title, toolName);
    const key = `${r.dateLocal || ""}:${fam}`;
    if (!map.has(key)) {
      map.set(key, { key, fam, hops: [], dateLocal: r.dateLocal, kind: r.kind, index: r.index, raw: r.raw });
      order.push(key);
    }
    map.get(key).hops.push(r);
  }
  const groups = order.map((id) => {
    const g = map.get(id);
    const hops = [...g.hops].sort((a, b) => (parseClockMin(a.t0) || 0) - (parseClockMin(b.t0) || 0));
    const slim = [];
    hops.forEach((h) => {
      const prev = slim[slim.length - 1];
      const a = parseClockMin(h.t0);
      const pa = prev ? parseClockMin(prev.t0) : null;
      const same = prev && cleanLine(prev.title).toLowerCase() === cleanLine(h.title).toLowerCase();
      if (prev && same && a != null && pa != null && Math.abs(a - pa) <= 3) {
        const b = parseClockMin(h.t1);
        const pb = parseClockMin(prev.t1);
        if (b != null && (pb == null || b > pb)) prev.t1 = h.t1;
        return;
      }
      slim.push({ ...h });
    });
    const mins = mergedMins(slim);
    return {
      ...g,
      hops: slim,
      title: foldLabel(g.fam, slim[0]?.title, toolName),
      overview: sittingOverview({ ...slim[0], hops: slim, raw: slim[0]?.raw || g.raw }, toolName),
      t0: slim[0]?.t0,
      t1: slim[slim.length - 1]?.t1,
      minutes: mins,
      durationLabel: mins ? fmtMins(mins) : slim[0]?.durationLabel,
    };
  }).filter((g) => g.fam !== "other" || order.length === 1);
  return groups.sort((a, b) => (parseClockMin(a.t0) || 0) - (parseClockMin(b.t0) || 0));
}

function leadTitle(title) {
  return String(title || "").replace(/\s+and\s+\d+\s+more\s+pages?$/i, "").trim();
}

function stretchLead(st, toolName) {
  const raw = leadTitle(st?.title);
  const fam = foldKey(raw, toolName);
  if (fam && fam !== "other") return foldLabel(fam, raw, toolName);
  return cleanLine(raw) || (toolName ? `One sitting in ${toolName}` : "One sitting");
}

function windowsInRange(uid, day, t0, t1, activity, timeline, name) {
  const a = parseClockMin(t0);
  const b = parseClockMin(t1);
  const rows = [];
  for (const act of (activity || []).filter((x) => String(x.userId) === String(uid) && x.dateLocal === day)) {
    const wins = act.windows?.length ? act.windows : [];
    wins.forEach((w, wi) => {
      rows.push({
        key: `w-${day}-${wi}`,
        title: cleanLine(w.title) || `Time in ${name}`,
        t0: w.t0,
        t1: w.t1,
        minutes: w.minutes,
        dateLocal: day,
        url: w.url,
        site: siteFromTitle(w.title, name),
      });
    });
  }
  if (!rows.length) {
    (timeline || []).filter((e) => String(e.userId) === String(uid) && e.dateLocal === day).forEach((e, i) => {
      rows.push({
        key: `t-${i}`,
        title: cleanLine(e.title) || `Time in ${name}`,
        t0: e.t0,
        t1: e.t1,
        minutes: e.minutes,
        dateLocal: day,
        url: e.url,
        site: siteFromTitle(e.title, name),
      });
    });
  }
  if (a == null) return rows;
  const end = b != null && b > a ? b : a + 60;
  return rows.filter((h) => {
    const s = parseClockMin(h.t0);
    if (s == null) return true;
    const e = parseClockMin(h.t1) ?? (s + (Number(h.minutes) || 0));
    return e > a && s < end;
  });
}

function sittingClock(st) {
  const a = parseClockMin(st?.t0);
  const b = parseClockMin(st?.t1);
  if (a == null) return null;
  const end = b != null && b > a ? b : a + 30;
  const pad = 90;
  const win0 = Math.max(0, a - pad);
  const win1 = Math.min(1440, end + pad);
  const span = Math.max(1, win1 - win0);
  const hm = (v) => `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`;
  return {
    left: ((a - win0) / span) * 100,
    width: Math.max(10, ((end - a) / span) * 100),
    from: hm(win0),
    to: hm(win1),
    mins: end - a,
  };
}

function ToolStretchSolid({
  t, name, stretch, index, allStretches, activity, timeline, onJump, onBack,
}) {
  const navigate = useNavigate();
  const k = inks(t);
  const ppr = paper(t);
  const s = stretch;
  const acts = (s.activities || []).filter((a) => a && a.label);
  const totalWeight = acts.reduce((n, x) => n + (Number(x.weight) || 0), 0) || 1;
  const live = s.source === "live";
  const clock = sittingClock(s);
  const hops = React.useMemo(() => {
    const raw = windowsInRange(s.userId, s.dateLocal, s.t0, s.t1, activity, timeline, name);
    return foldSittings(raw, name).flatMap((g) => (g.hops || []).map((h) => ({ ...h, parent: g.title })));
  }, [s.userId, s.dateLocal, s.t0, s.t1, activity, timeline, name]);
  const sibs = (allStretches || []).map((st, i) => ({ st, i }))
    .filter(({ st }) => String(st.userId) === String(s.userId) && st.dateLocal === s.dateLocal);
  const local = Math.max(0, sibs.findIndex((x) => x.i === index));
  const total = sibs.length || (allStretches || []).length;
  const jumpRel = (dir) => {
    if (sibs.length > 1) {
      const next = sibs[local + dir];
      if (next) onJump(next.i);
      return;
    }
    onJump(index + dir);
  };
  const logTo = (() => {
    const qs = new URLSearchParams();
    if (s.userId) qs.set("userId", String(s.userId));
    if (s.dateLocal) qs.set("date", s.dateLocal);
    qs.set("tab", "insights");
    return `${DASHBOARD_BASE}/work-summary-new?${qs}`;
  })();
  const cycle = ["blue", "violet", "gold", "pink"];
  const NavBtn = ({ disabled, onClick, children }) => (
    <button type="button" disabled={disabled} onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 4, height: 32, padding: "0 11px",
      borderRadius: 999, border: `1px solid ${t.c.border}`, background: ppr.card,
      color: disabled ? t.c.textTertiary : t.c.text, cursor: disabled ? "default" : "pointer",
      fontFamily: "inherit", fontSize: 12.5, fontWeight: 650, opacity: disabled ? 0.45 : 1,
    }}>{children}</button>
  );

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <button type="button" onClick={onBack} style={{
          display: "inline-flex", alignItems: "center", gap: 4, height: 32, padding: "0 11px",
          borderRadius: 999, border: `1px solid ${t.c.border}`, background: ppr.card,
          color: t.c.text, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 650,
        }}><ChevronLeft size={14} /> {name}</button>
        <span style={{ fontSize: 13, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
          Sitting {sibs.length ? local + 1 : (Number.isFinite(index) ? index + 1 : "—")} of {total || "—"}
        </span>
        <span style={{ flex: 1 }} />
        <NavBtn disabled={sibs.length ? local <= 0 : index <= 0} onClick={() => jumpRel(-1)}>
          <ChevronLeft size={14} /> Prev
        </NavBtn>
        <NavBtn disabled={sibs.length ? local >= sibs.length - 1 : index >= (allStretches || []).length - 1} onClick={() => jumpRel(1)}>
          Next <ChevronRight size={14} />
        </NavBtn>
      </div>
      <div className="tgw-pair tgw-a tgw-a2" style={{
        display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 16, alignItems: "stretch",
        height: "calc(100vh - 320px)", maxHeight: "calc(100vh - 320px)",
      }}>
        <GPanel t={t} style={{
          margin: 0, background: t.isDark ? ppr.card : "#F7F9FC",
          display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", height: "100%",
        }}>
          <SectionBar t={t} tone="violet" icon={<FileText size={16} strokeWidth={2.2} />} title="What happened" info="attention"
            bits={[
              { icon: <Layers size={12} strokeWidth={2.2} />, label: hops.length ? `${hops.length} page${hops.length === 1 ? "" : "s"}` : (acts.length ? `${acts.length} tool${acts.length === 1 ? "" : "s"}` : "no pages named") },
              { icon: s.unclear ? <AlertTriangle size={12} strokeWidth={2.2} /> : <Check size={12} strokeWidth={2.2} />, label: s.unclear ? "not yet analysed" : "written up" },
            ]} />
          <div className="tgw-alerts-scroll" style={{ flex: 1, minHeight: 0 }}>
          {s.summary ? (
            <Prose text={s.summary} numbered activities={acts} />
          ) : live ? (
            <LiveStretchFacts t={t} s={s} userId={s.userId} embedded />
          ) : (
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: t.c.textSecondary }}>
              Nothing in this sitting was distinctive enough to name. The time is real; the write-up is honestly absent.
            </p>
          )}
          {!live && acts.length > 0 ? (
            <div className="tgw-list" style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 16 }}>
              {acts.map((a, i) => {
                const pct = Number(a.weight) > 0 ? Math.round((a.weight / totalWeight) * 100) : null;
                return (
                  <div key={i} className="tgw-row" style={{
                    display: "grid", gridTemplateColumns: "32px 1fr 48px", gap: 10, alignItems: "center",
                    padding: "8px 4px", borderRadius: 10,
                  }}>
                    <ToolIcon name={a.label} type={a.type} size={30} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13, fontWeight: 550, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.label}</span>
                      {a.detail ? <span style={{ display: "block", fontSize: 12, color: t.c.textTertiary, marginTop: 2 }}>{a.detail}</span> : null}
                      {pct != null ? (
                        <span style={{ display: "block", height: 5, marginTop: 5, borderRadius: 99, background: ppr.well, overflow: "hidden" }}>
                          <i style={{ display: "block", height: "100%", width: `${pct}%`, borderRadius: 99, background: k.blue }} />
                        </span>
                      ) : null}
                    </span>
                    <b style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", textAlign: "right", color: t.c.textSecondary, fontWeight: 500 }}>{pct != null ? `${pct}%` : ""}</b>
                  </div>
                );
              })}
            </div>
          ) : null}
          {hops.length > 0 ? (
            <div className="tgw-tl" style={{ marginTop: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 650, color: t.c.text, margin: "0 0 8px" }}>Pages in this sitting</div>
              <div className="tgw-list" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {hops.map((hop, hi) => {
                  const ink = k[cycle[hi % cycle.length]] || k.blue;
                  const mins = hopMins(hop);
                  return (
                    <div key={hop.key || hi} className="tgw-row" style={{
                      display: "grid", gridTemplateColumns: "40px 1fr", gap: 10, alignItems: "center",
                      padding: "8px 8px", borderRadius: 12, background: t.isDark ? ppr.well : "#fff",
                    }}>
                      <span className="tgw-tl-card" aria-hidden="true" style={{
                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        background: ink, color: "#fff", gap: 1,
                      }}>
                        <span style={{ fontSize: 11.5, fontWeight: 750, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.3px", lineHeight: 1 }}>
                          {clockHM(hop.t0)}
                        </span>
                        <span style={{ fontSize: 9, fontWeight: 650, opacity: 0.82, lineHeight: 1.2 }}>
                          {mins ? fmtMins(mins) : "—"}
                        </span>
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{
                          display: "block", fontSize: 13.5, fontWeight: 650, letterSpacing: "-0.15px",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>{hopTopic(hop, hop.parent || name, hi, hops.length)}</span>
                        <span style={{
                          display: "block", marginTop: 3, fontSize: 12, color: t.c.textSecondary,
                          fontVariantNumeric: "tabular-nums",
                        }}>{clockRange(hop.t0, hop.t1, hopMins(hop) || hop.minutes) || clockHM(hop.t0)}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          </div>
        </GPanel>
        <aside style={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%", gap: 12 }}>
          <GPanel t={t} style={{ margin: 0, flexShrink: 0 }}>
            <SectionBar t={t} tone="blue" icon={<Clock size={16} strokeWidth={2.2} />} title="On the clock" info="rhythm"
              bits={[{ icon: <Clock size={12} strokeWidth={2.2} />, label: clock ? `${clock.mins} min` : "no clock times" }]} />
            {clock ? (
              <div>
                <div style={{ padding: "12px 14px", borderRadius: 12, background: ppr.well, marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 650, color: t.c.textSecondary }}>This sitting</div>
                  <div style={{ marginTop: 2, fontSize: 26, fontWeight: 750, letterSpacing: "-0.8px", fontVariantNumeric: "tabular-nums" }}>
                    {s.durationLabel || fmtMins(clock.mins)}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 14, fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>
                    {clockShort(s.t0)} → {clockShort(s.t1)}
                  </div>
                </div>
                <div style={{ position: "relative", height: 12, borderRadius: 99, background: ppr.well, overflow: "hidden" }}>
                  <i style={{
                    position: "absolute", top: 0, bottom: 0, left: `${clock.left}%`, width: `${clock.width}%`,
                    borderRadius: 99, background: k.blue,
                  }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: t.c.textTertiary, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
                  <span>{clock.from}</span>
                  <span>{clock.to}</span>
                </div>
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: t.c.textTertiary }}>This sitting has no clock times, so it cannot sit on the day.</p>
            )}
          </GPanel>
          {sibs.length > 1 ? (
            <GPanel t={t} style={{
              margin: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden",
            }}>
              <SectionBar t={t} tone="teal" icon={<Layers size={16} strokeWidth={2.2} />} title={`Also in ${name}`} info="focus"
                bits={[{ icon: <Layers size={12} strokeWidth={2.2} />, label: `${sibs.length} sittings` }]} />
              <div className="tgw-tl tgw-list tgw-alerts-scroll" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {sibs.map(({ st, i }, si) => {
                  const on = i === index;
                  const ink = k[cycle[si % cycle.length]] || k.blue;
                  return (
                    <button
                      key={i}
                      type="button"
                      className="tgw-row"
                      onClick={() => !on && onJump(i)}
                      style={{
                        display: "grid", gridTemplateColumns: "40px 1fr", gap: 10, alignItems: "center",
                        width: "100%", textAlign: "left", border: 0, padding: "8px 8px", borderRadius: 12,
                        cursor: on ? "default" : "pointer", fontFamily: "inherit", color: "inherit",
                        background: t.isDark ? ppr.well : "#fff",
                        boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
                        outline: on ? `2px solid ${k.blue}` : "none",
                        outlineOffset: -2,
                        position: "relative",
                      }}
                    >
                      {on ? (
                        <i aria-hidden="true" style={{
                          position: "absolute", left: 0, top: 8, bottom: 8, width: 3, borderRadius: "0 3px 3px 0", background: k.blue,
                        }} />
                      ) : null}
                      <span className="tgw-tl-card" aria-hidden="true" style={{
                        width: 40, height: 40, borderRadius: 10,
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        background: ink, color: "#fff", gap: 1,
                      }}>
                        <span style={{ fontSize: 11.5, fontWeight: 750, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{clockHM(st.t0)}</span>
                        <span style={{ fontSize: 9, fontWeight: 650, opacity: 0.82 }}>{st.durationLabel || "—"}</span>
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{
                          display: "block", fontSize: 13.5, fontWeight: 650,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>{stretchLead(st, name)}</span>
                        <span style={{ display: "block", marginTop: 3, fontSize: 12, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                          {clockRange(st.t0, st.t1, hopMins(st) || st.minutes) || clockHM(st.t0)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </GPanel>
          ) : null}
          <button type="button" onClick={() => navigate(logTo)} style={{
            display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
            padding: "15px 14px", borderRadius: 14, cursor: "pointer", flexShrink: 0,
            fontFamily: "inherit", color: "#fff", border: 0, background: k.blue,
            boxShadow: `0 8px 20px -10px ${k.blue}`,
          }}>
            <span aria-hidden="true" style={{
              width: 40, height: 40, borderRadius: 11, flexShrink: 0,
              display: "grid", placeItems: "center", background: "rgba(255,255,255,.18)",
            }}><BookOpen size={18} strokeWidth={2.2} /></span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 14, fontWeight: 650, letterSpacing: "-0.2px" }}>Work summary · insights</span>
              <span style={{ display: "block", fontSize: 12.5, opacity: 0.82, marginTop: 2 }}>
                {firstName(s.userName)}’s day
              </span>
            </span>
            <span style={{ display: "grid" }}><ChevronRight size={16} /></span>
          </button>
        </aside>
      </div>
    </>
  );
}

export function GraphicalTool({
  t, view, name, type, category, data, loading, people, coverage,
  person, personName, personId, day, sess,
  allStretches, activity = [], timeline = [], personStretchIdx, stretch, focus,
  crumbs, backTo, backLabel, subject, excludes,
  openStretch, openPerson, openPersonDay, openPersonFull, openAppUsage, onJumpStretch, onBackStretch, openSession,
}) {
  const kind = type === "website" ? "Website" : "Application";
  const onWriteup = view === "person" && day && sess;
  const onTimeline = view === "person" && day && !sess;

  const dayRows = React.useMemo(() => {
    if (!onTimeline || !personId || !day) return [];
    return foldSittings(
      sittingsForPerson(personId, allStretches, activity, timeline, name)
        .filter((r) => r.dateLocal === day),
      name,
    );
  }, [onTimeline, personId, day, allStretches, activity, timeline, name]);
  const dayMins = dayRows.reduce((n, r) => n + (Number(r.minutes) || 0), 0) || person?.minutes || 0;
  const dayShare = data?.totalMinutes ? Math.round((dayMins / data.totalMinutes) * 100) : 0;

  const title = onWriteup
    ? `${firstName(personName)} in ${name}`
    : onTimeline
      ? `${firstName(personName)} in ${name}`
      : view === "person" ? personName
        : view === "stretch" ? stretchLead(stretch, name)
          : name;

  const dayKeys = data?.dayKeys || [];
  const periodLabel = dayKeys.length === 1
    ? prettyDay(dayKeys[0])
    : dayKeys.length > 1
      ? `${prettyDay(dayKeys[0])} – ${prettyDay(dayKeys[dayKeys.length - 1])}`
      : null;
  const sitN = (allStretches?.length ? allStretches : activity)?.length || 0;
  const lead = [...(people || [])].sort((a, b) => (b.minutes || 0) - (a.minutes || 0))[0];
  const isDigest = view === "digest";
  const isStretch = view === "stretch";
  const stretchDay = stretch?.dateLocal ? prettyDay(stretch.dateLocal) : periodLabel;
  const digestSentence = loading ? "Reading the tool…" : (
    <>
      Tracked <b>{fmtMins(data?.totalMinutes || 0)}</b>
      {periodLabel ? <> on <b>{periodLabel}</b></> : null}.
      {people?.length ? (
        <>
          {" "}{people.length} {people.length === 1 ? "person" : "people"}
          {lead?.name ? <> — most of it from <b>{firstName(lead.name)}</b></> : null}.
        </>
      ) : " No one used it in this window."}
      {sitN ? <> {sitN} sitting{sitN === 1 ? "" : "s"}.</> : null}
    </>
  );

  return (
    <DrillFrame
      key={`${view}-${day || ""}-${stretch?.id || personStretchIdx || ""}`}
      t={t}
      title={title}
      icon={onTimeline || onWriteup
        ? <Avatar user={person} name={personName} size={50} />
        : (name ? <ToolIcon name={name} type={type} size={50} /> : null)}
      tone={view === "digest" ? "violet" : "blue"}
      eyebrow={onWriteup ? "Tool · one sitting" : onTimeline ? "Tool · one person · one day" : view === "person" ? "Tool · one person" : isStretch ? "Tool · one sitting" : `Tool · ${kind.toLowerCase()}`}
      chips={isDigest || isStretch || onTimeline || onWriteup ? [] : [
        name,
        view === "person" && person ? `${fmtMins(person.minutes)} in ${name}` : null,
      ]}
      sentence={isDigest ? digestSentence : isStretch && stretch ? (
        <>
          From <b>{clockShort(stretch.t0)}</b> to <b>{clockShort(stretch.t1)}</b>
          {stretchDay ? <> on <b>{stretchDay}</b></> : null}.
          {stretch.userName ? <> {firstName(stretch.userName)} in <b>{name}</b>.</> : null}
          {stretch.project ? <> On <b>{stretch.project}</b>.</> : " No project attributed."}
          {stretch.unclear ? " Not yet analysed." : null}
        </>
      ) : onTimeline ? (
        <>
          <b>{firstName(personName)}</b> spent <b>{fmtMins(dayMins)}</b> in <b>{name}</b>
          {day ? <> on <b>{prettyDay(day)}</b></> : null}.
          {dayRows.length ? <> {dayRows.length} sitting{dayRows.length === 1 ? "" : "s"}.</> : " No named sitting yet."}
        </>
      ) : onWriteup ? (
        <>
          <b>{firstName(personName)}</b> in <b>{name}</b>
          {day ? <> on <b>{prettyDay(day)}</b></> : null}.
        </>
      ) : undefined}
      readings={isDigest ? [
        { icon: <Clock size={16} strokeWidth={2.2} />, value: loading ? "—" : fmtMins(data?.totalMinutes || 0), label: periodLabel ? `tracked · ${periodLabel}` : "tracked", tone: "blue" },
        { icon: <UsersIcon size={16} strokeWidth={2.2} />, value: String(people?.length || 0), label: people?.length === 1 ? "person used it" : "people used it", tone: "teal" },
        { icon: <Layers size={16} strokeWidth={2.2} />, value: String(sitN), label: sitN === 1 ? "sitting" : "sittings", tone: "violet" },
      ] : isStretch && stretch ? [
        { icon: <Clock size={16} strokeWidth={2.2} />, value: stretch.durationLabel || "—", label: "this sitting", tone: "blue" },
        { icon: <Play size={16} strokeWidth={2.2} />, value: clockShort(stretch.t0), label: "started", tone: "teal" },
        { icon: <Pause size={16} strokeWidth={2.2} />, value: clockShort(stretch.t1), label: "ended", tone: "gold" },
      ] : onTimeline ? [
        { icon: <Clock size={16} strokeWidth={2.2} />, value: fmtMins(dayMins), label: `in ${name}`, tone: "blue" },
        { icon: <Layers size={16} strokeWidth={2.2} />, value: String(dayRows.length || "—"), label: dayRows.length === 1 ? "sitting" : "sittings", tone: "teal" },
        { icon: <UsersIcon size={16} strokeWidth={2.2} />, value: dayShare ? `${dayShare}%` : "—", label: "of all time in it", tone: "gold" },
      ] : undefined}
      subtitle={onWriteup
        ? `${firstName(personName)} in ${name}`
        : onTimeline
          ? `${firstName(personName)} in ${name}`
          : view === "person"
            ? `In ${name} · ${kind}`
            : isStretch
              ? stretchDay || name
              : periodLabel || kind}
      crumbs={crumbs}
      backTo={backTo}
      backLabel={backLabel}
      subject={isDigest || isStretch || onTimeline || onWriteup ? [] : subject}
      excludes={excludes}
      coverage={coverage && coverage.covered < coverage.total
        ? `based on activity detail from ${coverage.covered} of ${coverage.total}`
        : null}
      instrument={null}
    >
      <DashAntd>
        {loading ? (
          <GPanel t={t} className="tgw-a tgw-a1">
            <PanelHead t={t} title={`Opening ${name}…`} subtitle="fetching the record" info="focus" />
            <SkeletonRows count={6} />
          </GPanel>
        ) : !data || data.empty ? (
          <GPanel t={t}>
            <div style={{ padding: 28, fontSize: 13, color: t.c.textSecondary, lineHeight: 1.6 }}>
              <b style={{ color: t.c.text }}>{name ? `No per-person detail for ${name} in this window.` : "No tool in this link."}</b>
            </div>
          </GPanel>
        ) : view === "stretch" ? (
          stretch ? (
            <ToolStretchSolid
              t={t}
              name={name}
              stretch={stretch}
              index={focus}
              allStretches={allStretches}
              activity={activity}
              timeline={timeline}
              onJump={onJumpStretch}
              onBack={onBackStretch}
            />
          ) : (
            <GPanel t={t}><div style={{ padding: 24, color: t.c.textTertiary }}>That sitting is no longer in this window.</div></GPanel>
          )
        ) : onWriteup ? (
          <ToolWriteup
            t={t}
            name={name}
            type={type}
            personName={personName}
            personId={personId}
            day={day}
            sess={sess}
          />
        ) : onTimeline ? (
          <ToolSession
            t={t}
            name={name}
            person={person}
            personName={personName}
            personId={personId}
            day={day}
            sittings={dayRows}
            minutes={dayMins}
            people={people}
            activity={activity}
            openStretch={openStretch}
            openPerson={openPerson}
            openSession={openSession}
          />
        ) : view === "person" ? (
          <ToolPerson
            t={t} name={name} person={person} personName={personName} personId={personId} data={data}
            idxs={personStretchIdx} stretches={allStretches}
            activity={(activity || []).filter((a) => String(a.userId) === String(personId))}
            openStretch={openStretch}
            openPersonDay={openPersonDay}
            openPersonFull={openPersonFull}
            openSession={openSession}
          />
        ) : (
          <ToolDigest
            t={t} name={name} data={data} people={people} stretches={allStretches}
            activity={activity}
            timeline={timeline}
            openStretch={openStretch}
            openPersonDay={openPersonDay}
          />
        )}
      </DashAntd>
    </DrillFrame>
  );
}

function DoneCards({ t, rows, onOpen }) {
  const colors = toolColors(t);
  const ppr = paper(t);
  const mx = Math.max(...(rows || []).map((r) => r.minutes || 1), 1);
  if (!rows?.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.slice(0, 8).map((st, i) => (
        <button
          key={`${st.userId}-${st.dateLocal || st.day}-${i}`}
          type="button"
          onClick={() => onOpen?.(st, i)}
          style={{
            display: "grid", gridTemplateColumns: "4px 1fr auto", gap: 12, alignItems: "center",
            width: "100%", textAlign: "left", border: `1px solid ${t.c.border}`,
            background: ppr.well, borderRadius: t.r.well, padding: "12px 14px",
            cursor: "pointer", fontFamily: "inherit", color: "inherit",
          }}
        >
          <i style={{ display: "block", alignSelf: "stretch", borderRadius: 99, background: colors[i % colors.length] }} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 14, fontWeight: 700 }}>{st.title}</span>
            <span style={{ fontSize: 12, color: t.c.textSecondary }}>
              {[st.userName, st.day || st.dateLocal, st.t0].filter(Boolean).join(" · ")}
            </span>
            <span style={{
              display: "block", height: 7, borderRadius: 99, background: t.c.border,
              overflow: "hidden", marginTop: 8,
            }}>
              <i className="tgw-growX" style={{
                display: "block", height: "100%",
                width: `${Math.max(8, ((st.minutes || 1) / mx) * 100)}%`,
                background: colors[i % colors.length], borderRadius: 99,
              }} />
            </span>
          </span>
          <span style={{ fontSize: 14, fontWeight: 750, fontVariantNumeric: "tabular-nums", color: colors[i % colors.length] }}>
            {st.durationLabel || fmtMins(st.minutes)} ›
          </span>
        </button>
      ))}
    </div>
  );
}


function hookForPerson(uid, stretches, activity, name) {
  const st = (stretches || []).find((x) => String(x.userId) === String(uid) && (x.summary || x.title));
  if (st?.summary) return firstSentence(st.summary);
  if (st?.title && !isGenericTitle(st.title, name)) return siteFromTitle(st.title, name) || cleanLine(st.title);
  const act = (activity || []).find((x) => String(x.userId) === String(uid));
  const win = act?.windows?.[0]?.title || act?.title;
  if (win && !isGenericTitle(win, name)) return siteFromTitle(win, name) || cleanLine(win);
  return null;
}

function sitFace(st, toolName) {
  const over = st?.overview || sittingOverview(st, toolName);
  const named = cleanLine(st?.title);
  const kicker = named && !isGenericTitle(named, toolName) && named !== over ? named : "";
  return { kicker, head: over };
}

function ToolDigest({ t, name, data, people, stretches, activity = [], timeline = [], openStretch, openPersonDay }) {
  const navigate = useNavigate();
  const k = inks(t);
  const ppr = paper(t);
  const ranked = [...(people || [])].sort((a, b) => (b.minutes || 0) - (a.minutes || 0));
  const [selId, setSelId] = React.useState(() => ranked[0] ? String(ranked[0].userId) : null);
  React.useEffect(() => {
    if (!people?.length) { setSelId(null); return; }
    if (!people.some((p) => String(p.userId) === String(selId))) setSelId(String(people[0].userId));
  }, [people, selId]);

  const person = ranked.find((p) => String(p.userId) === String(selId)) || ranked[0] || null;
  const sittings = React.useMemo(
    () => (person ? foldSittings(sittingsForPerson(person.userId, stretches, activity, timeline, name), name) : []),
    [person, stretches, activity, timeline, name],
  );
  const [openKey, setOpenKey] = React.useState(null);
  React.useEffect(() => { setOpenKey(null); }, [selId]);

  const first = person ? firstName(person.name) : "They";
  const tracked = Math.max(
    Number(person?.minutes) || 0,
    sittings.reduce((n, s) => n + sittingMins(s), 0),
  );
  const crux = (() => {
    if (!person) return null;
    const insights = data.insights || [];
    const hit = insights.find((e) => {
      const who = String(e.userName || "").toLowerCase();
      return who.startsWith(first.toLowerCase()) || String(e.userId) === String(person.userId);
    });
    if (hit?.text) return hit.title && !isGenericTitle(hit.title, name) ? `${hit.title} — ${hit.text}` : hit.text;
    const sum = sittings.find((s) => s.raw?.summary)?.raw.summary;
    if (sum) return firstSentence(sum);
    const face = sittings[0] ? sitFace(sittings[0], name) : null;
    return face?.head && !isGenericTitle(face.head, name) ? face.head : null;
  })();

  const day = sittings[0]?.dateLocal || (data.dayKeys || [])[0];
  const logTo = (() => {
    const qs = new URLSearchParams();
    if (person?.userId) qs.set("userId", String(person.userId));
    if (day) qs.set("date", day);
    qs.set("tab", "insights");
    return `${DASHBOARD_BASE}/work-summary-new?${qs}`;
  })();
  const cycle = ["blue", "violet", "gold", "pink"];
  const clamp2 = {
    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
  };

  return (
    <div className="tgw-pair tgw-a tgw-a2" style={{
      display: "grid", gridTemplateColumns: "300px minmax(0,1fr)", gap: 16, alignItems: "start",
    }}>
      <GPanel t={t} style={{
        margin: 0, display: "flex", flexDirection: "column", minHeight: 0,
        maxHeight: "calc(100vh - 268px)", overflow: "hidden",
      }}>
        <SectionBar t={t} tone="blue" icon={<UsersIcon size={16} strokeWidth={2.2} />} title="Who used it" info="people"
          bits={[{ icon: <UsersIcon size={12} strokeWidth={2.2} />, label: ranked.length ? `${ranked.length} ${ranked.length === 1 ? "person" : "people"}` : "no one yet" }]} />
        <div className="tgw-list" style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
          {ranked.map((p, i) => {
            const ink = k[cycle[i % cycle.length]] || k.blue;
            const on = String(p.userId) === String(person?.userId);
            const hook = hookForPerson(p.userId, stretches, activity, name)
              || p.position
              || `${fmtMins(p.minutes)} in ${name}`;
            return (
              <button
                key={p.userId || i}
                type="button"
                className="tgw-row"
                onClick={() => setSelId(String(p.userId))}
                style={{
                  display: "grid", gridTemplateColumns: "36px 1fr auto", gap: 10, alignItems: "center",
                  width: "100%", textAlign: "left", border: 0, padding: "10px 10px", borderRadius: 12,
                  cursor: "pointer", fontFamily: "inherit", color: "inherit", position: "relative",
                  background: on ? (t.isDark ? ppr.well : "#fff") : "transparent",
                  boxShadow: on && !t.isDark ? "0 1px 2px rgba(24,32,46,.08)" : "none",
                }}
              >
                {on ? (
                  <i aria-hidden="true" style={{
                    position: "absolute", left: 0, top: 10, bottom: 10, width: 3, borderRadius: "0 3px 3px 0", background: k.blue,
                  }} />
                ) : null}
                <span aria-hidden="true" style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  display: "grid", placeItems: "center", overflow: "hidden",
                }}>
                  <Avatar user={p} name={p.name} size={36} />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{
                    display: "block", fontSize: 13.5, fontWeight: 650,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{p.name}</span>
                  <span style={{
                    display: "block", marginTop: 2, fontSize: 12, color: t.c.textTertiary,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{hook}</span>
                </span>
                <span style={{
                  fontSize: 12.5, fontWeight: 650, fontVariantNumeric: "tabular-nums",
                  color: on ? k.blue : t.c.textSecondary,
                }}>{fmtMins(Number(p.minutes) > 0 ? p.minutes : (String(p.userId) === String(person?.userId) ? tracked : p.minutes))}</span>
              </button>
            );
          })}
        </div>
      </GPanel>

      <GPanel t={t} style={{
        margin: 0, display: "flex", flexDirection: "column", minHeight: 0,
        maxHeight: "calc(100vh - 268px)", overflow: "hidden", height: "auto",
      }}>
        <SectionBar t={t} tone="violet" icon={<Layers size={16} strokeWidth={2.2} />} title={person ? `${first} in ${name}` : "What they did"} info="focus"
          bits={person ? [
            { icon: <Clock size={12} strokeWidth={2.2} />, label: fmtMins(tracked) },
            { icon: <Layers size={12} strokeWidth={2.2} />, label: `${sittings.length} ${isBrowserTool(name) ? (sittings.length === 1 ? "site" : "sites") : (sittings.length === 1 ? "sitting" : "sittings")}` },
          ] : []} />
        <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {person ? (
            <div key={person.userId} className="tgw-a">
              {crux ? (
                <p style={{ margin: "0 0 14px", fontSize: 15, lineHeight: 1.55, color: t.c.text }}>{crux}</p>
              ) : null}
              {sittings.length ? (
                <div className="tgw-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {sittings.map((st, i) => {
                    const ink = k[cycle[i % cycle.length]] || k.blue;
                    const many = (st.hops || []).length > 1;
                    const open = openKey === st.key;
                    const face = sitFace(st, name);
                    const tile = sitTile(st);
                    const openHop = (hop) => {
                      if (hop?.kind === "stretch") openStretch?.(hop.index);
                      else openPersonDay?.(person.userId, person.name, hop?.dateLocal || st.dateLocal);
                    };
                    return (
                      <div key={st.key} style={{
                        borderRadius: 14,
                        background: t.isDark ? ppr.well : "#fff",
                        boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
                        overflow: "hidden",
                      }}>
                        <button
                          type="button"
                          className="tgw-row"
                          onClick={() => (many ? setOpenKey(open ? null : st.key) : openHop(st.hops?.[0] || st))}
                          style={{
                            display: "grid", gridTemplateColumns: "48px minmax(0,1fr) auto 18px", gap: 12, alignItems: "center",
                            width: "100%", textAlign: "left", border: 0, padding: "11px 12px",
                            cursor: "pointer", fontFamily: "inherit", color: "inherit", background: "transparent",
                          }}
                        >
                          <span aria-hidden="true" style={{
                            width: 48, height: 48, borderRadius: 11, flexShrink: 0,
                            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                            background: ink, color: "#fff", gap: 1,
                          }}>
                            <span style={{ fontSize: 13, fontWeight: 750, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.4px", lineHeight: 1 }}>
                              {tile.top}
                            </span>
                            {tile.bot ? (
                              <span style={{ fontSize: 10, fontWeight: 650, opacity: 0.82, lineHeight: 1.2 }}>
                                {tile.bot}
                              </span>
                            ) : null}
                          </span>
                          <span style={{ minWidth: 0 }}>
                            {face.kicker ? (
                              <span style={{ display: "block", fontSize: 12, fontWeight: 650, color: t.c.textSecondary, marginBottom: 2 }}>{face.kicker}</span>
                            ) : null}
                            <span style={{ ...clamp2, fontSize: 14.5, fontWeight: 650, letterSpacing: "-0.2px", lineHeight: 1.35 }}>
                              {face.head}
                            </span>
                            <span style={{ display: "block", marginTop: 5, fontSize: 12.5, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                              {sitWhenLine(st)}
                              {many ? ` · ${st.hops.length} visits` : ""}
                            </span>
                          </span>
                          <span style={{
                            fontSize: 13, fontWeight: 650, fontVariantNumeric: "tabular-nums",
                            color: t.c.textSecondary, whiteSpace: "nowrap",
                          }}>{fmtMins(sittingMins(st))}</span>
                          <span style={{ color: t.c.textTertiary, display: "grid" }}>
                            {many ? <ChevronDown size={16} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} /> : <ChevronRight size={16} />}
                          </span>
                        </button>
                        {many && open ? (
                          <div className="tgw-alerts-scroll" style={{
                            margin: "0 12px 12px", padding: "8px 0 0",
                            borderTop: `1px solid ${t.c.border}`,
                            display: "flex", flexDirection: "column", gap: 6,
                            maxHeight: 220,
                          }}>
                            {st.hops.map((hop, hi) => {
                              const topic = hopTopic(hop, st.title, hi, st.hops.length);
                              const mins = hopMins(hop);
                              return (
                                <button
                                  key={hop.key || hi}
                                  type="button"
                                  className="tgw-row"
                                  onClick={() => openHop(hop)}
                                  style={{
                                    display: "grid", gridTemplateColumns: "40px 1fr 18px", gap: 10, alignItems: "center",
                                    width: "100%", textAlign: "left", border: 0, padding: "8px 8px",
                                    borderRadius: 12, cursor: "pointer", fontFamily: "inherit", color: "inherit",
                                    background: t.isDark ? ppr.card : ppr.well,
                                  }}
                                >
                                  <span aria-hidden="true" style={{
                                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                    background: ink, color: "#fff", gap: 1,
                                  }}>
                                    <span style={{ fontSize: 11.5, fontWeight: 750, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.3px", lineHeight: 1 }}>
                                      {clockHM(hop.t0)}
                                    </span>
                                    <span style={{ fontSize: 9, fontWeight: 650, opacity: 0.82, lineHeight: 1.2 }}>
                                      {mins ? fmtMins(mins) : "—"}
                                    </span>
                                  </span>
                                  <span style={{ minWidth: 0 }}>
                                    <span style={{
                                      display: "block", fontSize: 13.5, fontWeight: 650, letterSpacing: "-0.15px",
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}>{topic}</span>
                                    <span style={{
                                      display: "block", marginTop: 3, fontSize: 12, color: t.c.textSecondary,
                                      fontVariantNumeric: "tabular-nums",
                                    }}>{clockRange(hop.t0, hop.t1, hopMins(hop) || hop.minutes) || clockHM(hop.t0)}</span>
                                  </span>
                                  <span style={{ color: t.c.textTertiary, display: "grid" }}><ChevronRight size={15} /></span>
                                </button>
                              );
                            })}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: 13.5, color: t.c.textSecondary, lineHeight: 1.55 }}>
                  {fmtMins(person.minutes)} in {name} is real. No sitting we can write up yet.
                </p>
              )}
              <button type="button" onClick={() => navigate(logTo)} style={{
                display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
                padding: "14px 14px", borderRadius: 14, cursor: "pointer", marginTop: 14,
                fontFamily: "inherit", color: "#fff", border: 0, background: k.blue,
              }}>
                <span aria-hidden="true" style={{
                  width: 40, height: 40, borderRadius: 11, flexShrink: 0,
                  display: "grid", placeItems: "center", background: "rgba(255,255,255,.18)",
                }}><BookOpen size={18} strokeWidth={2.2} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 650 }}>Work summary · insights</span>
                  <span style={{ display: "block", fontSize: 12.5, opacity: 0.82, marginTop: 2 }}>{first}’s day</span>
                </span>
                <span style={{ display: "grid" }}><ChevronRight size={16} /></span>
              </button>
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 13.5, color: t.c.textSecondary }}>No one used {name} in this window.</p>
          )}
        </div>
      </GPanel>
    </div>
  );
}

function ToolPerson({ t, name, person, personName, personId, data, idxs, stretches, activity = [], openStretch, openPersonDay, openPersonFull, openSession }) {
  const k = inks(t);
  const share = data.totalMinutes ? Math.round(((person?.minutes || 0) / data.totalMinutes) * 100) : 0;
  const written = idxs.map((i) => stretches[i]);
  const days = activity;
  const peopleCount = (data.people || []).length;

  return (
    <>
      <KpiStrip t={t} items={[
        { label: "Their time", value: person ? fmtMins(person.minutes) : "—", sub: `in ${name}` },
        { label: "Share", value: `${share}%`, sub: "of this tool" },
        { label: "People", value: String(peopleCount), sub: "used it in the window" },
        { label: "Days", value: String(days.length), sub: "click a day below" },
      ]} />

      {personId && days[0]?.dateLocal ? (
        <GPanel t={t} className="tgw-card tgw-a tgw-a1" style={{ marginBottom: 12 }}>
          <PanelHead t={t} title="What it was used for" subtitle={`${days[0].dateLocal} · click a purpose or session`} info="focus" />
          <WorkAppAnalysis
            t={t}
            name={name}
            personId={personId}
            day={days[0].dateLocal}
            onOpenSession={(sn) => openSession?.(personId, personName, days[0].dateLocal, sn)}
          />
        </GPanel>
      ) : null}

      <GPanel t={t} className="tgw-card tgw-a tgw-a2" style={{ marginBottom: 12 }}>
        <PanelHead t={t} title={`Who used ${name}`} subtitle={`${peopleCount} people in this window`} info="people" />
        <RaceRows
          t={t}
          people={(data.people || []).map((p) => ({
            ...p,
            totalMinutes: p.minutes,
            activeMinutes: p.minutes,
            idleMinutes: 0,
          }))}
          onPerson={(p) => openPersonDay(p.userId, p.name, days.find((d) => String(d.userId) === String(p.userId))?.dateLocal)}
        />
      </GPanel>

      {days.length > 0 && (
        <GPanel t={t} className="tgw-card tgw-a tgw-a3" style={{ marginBottom: 12 }}>
          <PanelHead t={t} title="Days in this tool" subtitle="click a day for the timestamp timeline" info="rhythm" />
          <DoneCards
            t={t}
            rows={days}
            onOpen={(row) => openPersonDay(row.userId || person?.userId, personName, row.dateLocal)}
          />
        </GPanel>
      )}

      {written.length > 0 && (
        <GPanel t={t} className="tgw-card tgw-a tgw-a4">
          <PanelHead t={t} title="Written stretches" subtitle="on the clock · click for the full write-up" info="focus" />
          <TimeRail
            t={t}
            onOpen={(row) => openStretch(idxs[row.index])}
            rows={railFromStretches(written)}
          />
        </GPanel>
      )}

      {!days.length && !written.length && (
        <GPanel t={t}>
          <div style={{ padding: 8, fontSize: 13, color: t.c.textSecondary }}>
            {person ? fmtMins(person.minutes) : "Time"} in {name} is real. No day-level breakdown arrived for them.
          </div>
        </GPanel>
      )}

      <div style={{ marginTop: 16, fontSize: 12.5 }}>
        <button type="button" onClick={openPersonFull} style={{
          border: 0, background: "none", color: k.blue, fontWeight: 650, cursor: "pointer", fontFamily: t.font.family,
        }}>
          Open {personName.split(" ")[0]}’s full picture ›
        </button>
      </div>
    </>
  );
}

/** "2026-08-22" → "Aug 22" — an ISO stamp is an id, not a headline. */
function humanDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!m) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[Number(m[2]) - 1]} ${Number(m[3])}`;
}

function ToolWriteup({ t, name, personName, personId, day, sess }) {
  const navigate = useNavigate();
  const sf = useSurface();
  const k = inks(t);
  const ppr = paper(t);
  const first = firstName(personName);
  const logTo = (() => {
    const qs = new URLSearchParams();
    if (personId) qs.set("userId", String(personId));
    if (day) qs.set("date", day);
    qs.set("tab", "insights");
    return `${DASHBOARD_BASE}/work-summary-new?${qs}`;
  })();
  return (
    <div className="tgw-pair tgw-a tgw-a2" style={{
      display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 16, alignItems: "start",
    }}>
      <GPanel t={t} style={{ margin: 0, background: t.isDark ? ppr.card : "#F7F9FC" }}>
        <SectionBar t={t} tone="violet" icon={<FileText size={16} strokeWidth={2.2} />} title="What happened" info="attention"
          bits={[{ icon: <Layers size={12} strokeWidth={2.2} />, label: name }]} />
        <WorkWriteup
          t={t}
          sf={sf}
          tone={k.blue}
          userId={personId}
          date={day}
          title={`${name} session`}
          detail={`What ${personName} did in ${name}`}
          blockStarts={[Number(sess)]}
          compact
        />
      </GPanel>
      <aside>
        <button type="button" onClick={() => navigate(logTo)} style={{
          display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
          padding: "15px 14px", borderRadius: 14, cursor: "pointer",
          fontFamily: "inherit", color: "#fff", border: 0, background: k.blue,
          boxShadow: `0 8px 20px -10px ${k.blue}`,
        }}>
          <span aria-hidden="true" style={{
            width: 40, height: 40, borderRadius: 11, flexShrink: 0,
            display: "grid", placeItems: "center", background: "rgba(255,255,255,.18)",
          }}><BookOpen size={18} strokeWidth={2.2} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 14, fontWeight: 650 }}>Work summary · insights</span>
            <span style={{ display: "block", fontSize: 12.5, opacity: 0.82, marginTop: 2 }}>{first}’s day</span>
          </span>
          <span style={{ display: "grid" }}><ChevronRight size={16} /></span>
        </button>
      </aside>
    </div>
  );
}

function ToolSession({
  t, name, person, personName, personId, day, sittings, minutes, people, activity,
  openStretch, openPerson, openSession,
}) {
  const navigate = useNavigate();
  const k = inks(t);
  const ppr = paper(t);
  const first = firstName(personName);
  const rows = sittings || [];
  const clock = sittingClock({ t0: rows[0]?.t0, t1: rows[rows.length - 1]?.t1 || rows[0]?.t1 });
  const others = (people || []).filter((p) => String(p.userId) !== String(personId)).slice(0, 6);
  const cycle = ["blue", "violet", "gold", "pink"];
  const logTo = (() => {
    const qs = new URLSearchParams();
    if (personId) qs.set("userId", String(personId));
    if (day) qs.set("date", day);
    qs.set("tab", "insights");
    return `${DASHBOARD_BASE}/work-summary-new?${qs}`;
  })();
  const openSit = (st) => {
    const hop = st.hops?.[0] || st;
    if (hop?.kind === "stretch" && Number.isFinite(hop.index)) {
      openStretch?.(hop.index);
      return;
    }
    const ms = startMsOf(st.dateLocal || day, st.t0);
    if (ms) openSession?.(personId, personName, day, { startMs: ms });
  };

  return (
    <div className="tgw-pair tgw-a tgw-a2" style={{
      display: "grid", gridTemplateColumns: "minmax(0,1fr) 300px", gap: 16, alignItems: "start",
    }}>
      <GPanel t={t} style={{ margin: 0, background: t.isDark ? ppr.card : "#F7F9FC" }}>
        <SectionBar t={t} tone="violet" icon={<Layers size={16} strokeWidth={2.2} />} title={`${first} in ${name}`} info="focus"
          bits={[
            { icon: <Clock size={12} strokeWidth={2.2} />, label: fmtMins(minutes) },
            { icon: <Layers size={12} strokeWidth={2.2} />, label: `${rows.length} ${rows.length === 1 ? "sitting" : "sittings"}` },
          ]} />
        {rows.length ? (
          <div className="tgw-tl tgw-list tgw-alerts-scroll" style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "calc(100vh - 360px)" }}>
            {rows.map((st, i) => {
              const ink = k[cycle[i % cycle.length]] || k.blue;
              const face = sitFace(st, name);
              const tile = sitTile(st);
              return (
                <button
                  key={st.key || i}
                  type="button"
                  className="tgw-row"
                  onClick={() => openSit(st)}
                  style={{
                    display: "grid", gridTemplateColumns: "48px 1fr 18px", gap: 12, alignItems: "center",
                    width: "100%", textAlign: "left", border: 0, padding: "11px 12px", borderRadius: 14,
                    cursor: "pointer", fontFamily: "inherit", color: "inherit",
                    background: t.isDark ? ppr.well : "#fff",
                    boxShadow: t.isDark ? "none" : "0 1px 2px rgba(24,32,46,.06)",
                  }}
                >
                  <span className="tgw-tl-card" aria-hidden="true" style={{
                    width: 48, height: 48, borderRadius: 11,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    background: ink, color: "#fff", gap: 1,
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 750, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.4px", lineHeight: 1 }}>
                      {tile.top}
                    </span>
                    {tile.bot ? (
                      <span style={{ fontSize: 10, fontWeight: 650, opacity: 0.82 }}>{tile.bot}</span>
                    ) : null}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    {face.kicker ? (
                      <span style={{ display: "block", fontSize: 12, fontWeight: 650, color: t.c.textSecondary, marginBottom: 2 }}>{face.kicker}</span>
                    ) : null}
                    <span style={{
                      fontSize: 14.5, fontWeight: 650, letterSpacing: "-0.2px", lineHeight: 1.35,
                      display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                    }}>{face.head}</span>
                    <span style={{ display: "block", marginTop: 5, fontSize: 12.5, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums" }}>
                      {sitWhenLine(st)}
                    </span>
                  </span>
                  <span style={{ color: t.c.textTertiary, display: "grid" }}><ChevronRight size={16} /></span>
                </button>
              );
            })}
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: t.c.textSecondary }}>
            {fmtMins(minutes)} in {name} is real. No sitting we can name yet.
          </p>
        )}
      </GPanel>
      <aside>
        <GPanel t={t} style={{ marginBottom: 12 }}>
          <SectionBar t={t} tone="blue" icon={<Clock size={16} strokeWidth={2.2} />} title="On the clock" info="rhythm"
            bits={[{ icon: <Clock size={12} strokeWidth={2.2} />, label: clock ? `${clock.mins} min` : "no clock times" }]} />
          {clock ? (
            <div>
              <div style={{ padding: "12px 14px", borderRadius: 12, background: ppr.well, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 650, color: t.c.textSecondary }}>This day</div>
                <div style={{ marginTop: 2, fontSize: 26, fontWeight: 750, letterSpacing: "-0.8px", fontVariantNumeric: "tabular-nums" }}>
                  {fmtMins(minutes)}
                </div>
                <div style={{ marginTop: 4, fontSize: 14, fontWeight: 650, fontVariantNumeric: "tabular-nums" }}>
                  {clockRange(rows[0]?.t0, rows[rows.length - 1]?.t1 || rows[0]?.t1, minutes)}
                </div>
              </div>
              <div style={{ position: "relative", height: 12, borderRadius: 99, background: ppr.well, overflow: "hidden" }}>
                <i style={{
                  position: "absolute", top: 0, bottom: 0, left: `${clock.left}%`, width: `${clock.width}%`,
                  borderRadius: 99, background: k.blue,
                }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: t.c.textTertiary, marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
                <span>{clock.from}</span>
                <span>{clock.to}</span>
              </div>
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: t.c.textTertiary }}>No clock times on these sittings.</p>
          )}
        </GPanel>
        {others.length ? (
          <GPanel t={t} style={{ marginBottom: 12 }}>
            <SectionBar t={t} tone="teal" icon={<UsersIcon size={16} strokeWidth={2.2} />} title={`Also in ${name}`} info="people"
              bits={[{ icon: <UsersIcon size={12} strokeWidth={2.2} />, label: `${others.length}` }]} />
            <div className="tgw-list" style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {others.map((p) => (
                <button
                  key={p.userId}
                  type="button"
                  className="tgw-row"
                  onClick={() => openPerson?.(p.userId, p.name)}
                  style={{
                    display: "grid", gridTemplateColumns: "32px 1fr auto", gap: 10, alignItems: "center",
                    width: "100%", textAlign: "left", border: 0, padding: "8px 6px", borderRadius: 10,
                    cursor: "pointer", fontFamily: "inherit", color: "inherit", background: "transparent",
                  }}
                >
                  <Avatar user={p} name={p.name} size={32} />
                  <span style={{ fontSize: 13, fontWeight: 550, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                  <span style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", color: t.c.textSecondary }}>{fmtMins(p.minutes)}</span>
                </button>
              ))}
            </div>
          </GPanel>
        ) : null}
        <button type="button" onClick={() => navigate(logTo)} style={{
          display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
          padding: "15px 14px", borderRadius: 14, cursor: "pointer",
          fontFamily: "inherit", color: "#fff", border: 0, background: k.blue,
          boxShadow: `0 8px 20px -10px ${k.blue}`,
        }}>
          <span aria-hidden="true" style={{
            width: 40, height: 40, borderRadius: 11, flexShrink: 0,
            display: "grid", placeItems: "center", background: "rgba(255,255,255,.18)",
          }}><BookOpen size={18} strokeWidth={2.2} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 14, fontWeight: 650 }}>Work summary · insights</span>
            <span style={{ display: "block", fontSize: 12.5, opacity: 0.82, marginTop: 2 }}>{first}’s day</span>
          </span>
          <span style={{ display: "grid" }}><ChevronRight size={16} /></span>
        </button>
      </aside>
    </div>
  );
}

export default GraphicalTool;
